import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Calendar, Clock, MapPin, Ticket, Share2, Mic2, ArrowLeft, ChevronRight, Users } from "lucide-react";
import { toast } from "sonner";
import { api, fileUrl } from "../lib/api";
import { ListingHeroImg } from "../lib/termImages";
import { PipeText } from "../lib/pipeText";
import { fmtDate, fmtTime } from "../components/Cards";
import { Reveal, SectionTitle, useSeo } from "../components/VIP";
import { ListingActions } from "../components/ListingActions";
import { Countdown, Sponsors } from "../components/Marketplace";
import { MerchGrid } from "../components/VenueExtras";
import { VideoGrid } from "../components/VideoGrid";
import { RsvpDialog } from "../components/RsvpDialog";
import { WeatherPanel } from "../components/Weather";

const MAP = (q) => `https://maps.google.com/maps?q=${encodeURIComponent(q)}&t=&z=13&ie=UTF8&iwloc=&output=embed`;

export default function EventDetail() {
  const { slug } = useParams();
  const [e, setE] = useState(null);
  const load = () => api.get(`/events/${slug}`).then((r) => setE(r.data)).catch(() => setE(false));
  useEffect(() => { load(); window.scrollTo(0, 0); }, [slug]);
  useSeo(e ? e.seo_title || `${e.title} | Gulf Coast VIP` : null, e?.description);

  if (e === false) return <div className="py-40 text-center text-zinc-500">Event not found.</div>;
  if (!e) return <div className="py-40 text-center text-zinc-500">Loading...</div>;

  const share = () => { navigator.clipboard.writeText(window.location.href); toast.success("Link copied!"); };
  const isFree = ["0", 0, "", "Free", "free", null].includes(e.ticket_price);
  const startIso = e.event_date ? `${e.event_date}T${(e.start_time || "20:00")}:00` : null;
  // End datetime: use end_date if provided; else if end_time is earlier than start_time
  // (overnight event, e.g., 9pm–2am), roll to the next calendar day so the event isn't
  // marked "ended" the moment it starts.
  const nextDay = (ymd) => { const d = new Date(`${ymd}T00:00:00`); d.setDate(d.getDate() + 1); return d.toISOString().slice(0, 10); };
  const endDay = e.end_date && e.end_date !== e.event_date
    ? e.end_date
    : (e.end_time && e.start_time && e.end_time < e.start_time ? nextDay(e.event_date) : e.event_date);
  const endIso = e.event_date && e.end_time ? `${endDay}T${e.end_time}:00` : startIso;
  const single = e.artists?.length === 1;
  const dateRange = e.end_date && e.end_date !== e.event_date ? `${fmtDate(e.event_date)} – ${fmtDate(e.end_date)}` : fmtDate(e.event_date);
  const cancelled = e.status === "cancelled";
  const upcomingSeries = (e.series_events || []).filter((s) => s.event_date >= new Date().toISOString().slice(0, 10) && s.status !== "cancelled");

  return (
    <div data-testid="event-detail" className="pb-24 lg:pb-0">
      <div className="relative min-h-[78vh] flex flex-col overflow-hidden grain">
        <ListingHeroImg ent={e} kind="event" className="absolute inset-0 w-full h-full object-cover hero-zoom" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,rgba(244,63,94,0.25),transparent_55%)]" />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-black/30" />
        <Link to="/events" className="relative z-10 self-start mt-6 ml-6 md:ml-12 lg:ml-20 flex items-center gap-1 text-sm text-zinc-300 hover:text-white"><ArrowLeft size={16} /> All events</Link>
        <div className="relative flex-1 px-6 md:px-12 lg:px-20 flex flex-col justify-end pb-12 pt-10">
          <motion.div initial={{ opacity: 0, y: 36 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }} className="max-w-4xl">
            <p className="overline mb-3">{e.genre} · {e.event_type}</p>
            <h1 className="font-display font-black text-4xl md:text-7xl leading-[0.95] text-shadow-lg">{e.title}</h1>
            <div className="flex flex-wrap gap-x-6 gap-y-2 mt-6 text-zinc-200">
              <span className="flex items-center gap-2"><Calendar size={16} className="text-rose-400" /> {dateRange}</span>
              <span className="flex items-center gap-2"><Clock size={16} className="text-rose-400" /> {fmtTime(e.start_time)}{e.end_time ? ` – ${fmtTime(e.end_time)}` : ""}</span>
              <Link to={e.venue ? `/venues/${e.venue.slug}` : "#"} className="flex items-center gap-2 hover:text-white"><MapPin size={16} className="text-rose-400" /> {e.venue_name}, {e.city}, {e.state}</Link>
            </div>
            <div className="mt-6"><ListingActions entityType="event" entityId={e.id} claimStatus={e.claim_status || "unclaimed"} verified={!!e.verified} /></div>
          </motion.div>
        </div>
      </div>

      {cancelled && (
        <div className="bg-rose-600/90 text-white text-center py-3 px-6 font-bold text-sm" data-testid="event-cancelled-banner">
          This event has been cancelled.
        </div>
      )}

      <div className="px-6 md:px-12 lg:px-20 py-14 grid lg:grid-cols-3 gap-12">
        <div className="lg:col-span-2 space-y-16">
          {/* Countdown banner */}
          <Reveal>
            <div className="bg-gradient-to-r from-rose-500/15 to-orange-500/10 border border-white/10 rounded-2xl p-6 flex flex-col sm:flex-row items-center justify-between gap-4">
              <div><p className="overline mb-1">Counting down</p><p className="font-display font-bold text-xl">Get ready</p></div>
              <Countdown to={startIso} liveUntil={endIso} endLabel="Event ended" />
            </div>
          </Reveal>

          {upcomingSeries.length > 0 && (
            <Reveal>
              <SectionTitle kicker="Recurring event" title="More dates" />
              <div className="grid sm:grid-cols-2 gap-3 mt-6" data-testid="event-series">
                {upcomingSeries.map((s) => (
                  <Link key={s.id} to={`/events/${s.slug}`} className="flex items-center gap-4 bg-[#111] border border-white/10 rounded-2xl p-4 card-hover" data-testid={`series-date-${s.slug}`}>
                    <Calendar size={18} className="text-rose-400 shrink-0" />
                    <div><p className="font-display font-bold">{fmtDate(s.event_date)}</p>{s.start_time && <p className="text-sm text-zinc-400">{fmtTime(s.start_time)}</p>}</div>
                  </Link>
                ))}
              </div>
            </Reveal>
          )}

          <Reveal>
            <SectionTitle kicker="The experience" title="About this event" />
            <PipeText text={e.description} className="text-zinc-300 leading-relaxed mt-6 text-lg" />
            <div className="flex flex-wrap gap-3 mt-6 text-sm">
              <span className="glass px-4 py-2 rounded-full">Age: {e.age_restriction}</span>
              <span className="glass px-4 py-2 rounded-full">{e.indoor_outdoor}</span>
              {e.family_friendly && <span className="glass px-4 py-2 rounded-full">Family friendly</span>}
            </div>
          </Reveal>

          {/* Single artist rich summary vs multi lineup */}
          {single ? (
            <Reveal>
              <SectionTitle kicker="Performing live" title="The Artist" />
              {(() => { const a = e.artists[0]; return (
                <div className="mt-6 grid sm:grid-cols-5 gap-6 bg-[#111] border border-white/10 rounded-2xl overflow-hidden">
                  <img src={fileUrl(a.hero_image || a.profile_image)} alt={a.name} className="sm:col-span-2 w-full h-full max-h-72 object-cover" />
                  <div className="sm:col-span-3 p-6">
                    <div className="flex items-center gap-3">
                      {a.logo && <img src={fileUrl(a.logo)} alt="" className="w-10 h-10 rounded-lg object-cover" />}
                      <div><p className="overline">{a.genre}</p><h3 className="font-display font-bold text-2xl">{a.name}</h3></div>
                    </div>
                    <p className="text-zinc-400 text-sm mt-3 line-clamp-3">{a.bio}</p>
                    <div className="flex items-center gap-2 mt-4">
                      <span className={`text-xs px-3 py-1 rounded-full ${a.booking_available ? "bg-emerald-500/20 text-emerald-300" : "bg-zinc-500/20 text-zinc-300"}`}>{a.booking_available ? "Available for booking" : "Contact for availability"}</span>
                    </div>
                    <Link to={`/artists/${a.slug}`} className="inline-flex items-center gap-1 mt-5 bg-rose-500 hover:bg-rose-600 font-bold px-5 py-2.5 rounded-full text-sm">View artist <ChevronRight size={15} /></Link>
                  </div>
                </div>
              ); })()}
            </Reveal>
          ) : e.artists?.length > 1 && (
            <Reveal>
              <SectionTitle kicker="Who's playing" title="The Lineup" />
              <div className="grid sm:grid-cols-2 gap-4 mt-6">
                {e.artists.map((a, i) => (
                  <Link key={a.id} to={`/artists/${a.slug}`} className="group relative flex items-center gap-4 bg-[#111] border border-white/10 rounded-2xl p-3 card-hover" data-testid={`lineup-${a.slug}`}>
                    <img src={fileUrl(a.profile_image || a.hero_image)} alt={a.name} className="w-20 h-20 rounded-xl object-cover" />
                    {a.logo && <img src={fileUrl(a.logo)} alt="" className="absolute top-2 left-2 w-7 h-7 rounded object-cover border border-white/20" />}
                    <div className="flex-1">
                      <p className="overline text-[0.6rem]">{i === 0 ? "Headliner" : "Support"}</p>
                      <p className="font-display font-bold text-lg">{a.name}</p>
                      <p className="text-sm text-zinc-400">{a.genre}</p>
                    </div>
                    <ChevronRight className="text-zinc-600 group-hover:text-rose-400 group-hover:translate-x-1 transition-all" size={20} />
                  </Link>
                ))}
              </div>
            </Reveal>
          )}

          {/* Event merch (manual + connected marketplace products) */}
          {((e.merch?.length || 0) + (e.marketplace_merch?.length || 0)) > 0 && <Reveal><MerchGrid items={[...(e.marketplace_merch || []), ...(e.merch || [])]} title="Event Merch" /></Reveal>}
          {e.ticket_options?.length > 0 && (
            <Reveal>
              <SectionTitle kicker="Tickets" title="Ticket Options" />
              <div className="grid sm:grid-cols-2 gap-4 mt-6" data-testid="ticket-options">
                {e.ticket_options.map((t, i) => (
                  <div key={`${t.name || "ticket"}-${i}`} className="bg-[#111] border border-white/10 rounded-2xl p-5 flex items-center justify-between gap-3">
                    <div><p className="font-display font-bold">{t.name}</p>{t.status && t.status !== "on_sale" && <p className="text-xs text-amber-400 capitalize">{String(t.status).replace("_", " ")}</p>}</div>
                    <div className="text-right"><p className="font-display font-black text-xl">{t.price ? `$${t.price}` : "Free"}</p>{t.link && t.status !== "sold_out" && <a href={t.link} target="_blank" rel="noreferrer" className="text-xs text-rose-400">Buy →</a>}</div>
                  </div>
                ))}
              </div>
            </Reveal>
          )}
          {e.gallery_images?.length > 0 && (
            <Reveal>
              <SectionTitle kicker="In frame" title="Gallery" />
              <div className="columns-2 md:columns-3 gap-4 space-y-4 mt-6" data-testid="event-gallery">{e.gallery_images.map((g, i) => <img key={`${g}-${i}`} src={fileUrl(g)} alt="" className="w-full rounded-2xl break-inside-avoid" />)}</div>
            </Reveal>
          )}

          {e.videos?.length > 0 && <Reveal><VideoGrid items={e.videos} /></Reveal>}

          {/* Venue */}
          {e.venue && (
            <Reveal>
              <SectionTitle kicker="The destination" title="Venue & Directions" />
              <Link to={`/venues/${e.venue.slug}`} className="group block rounded-2xl overflow-hidden bg-[#111] border border-white/10 card-hover mt-6" data-testid="event-venue-link">
                <div className="relative h-52 overflow-hidden">
                  <img src={fileUrl(e.venue.hero_image)} alt={e.venue.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black to-transparent" />
                  <div className="absolute bottom-0 p-5">
                    <p className="overline">{e.venue.venue_type}</p>
                    <h3 className="font-display font-bold text-2xl">{e.venue.name}</h3>
                    <p className="text-zinc-300 text-sm mt-1 flex items-center gap-1"><MapPin size={13} /> {e.venue.address}</p>
                  </div>
                </div>
              </Link>
              <div className="mt-5 rounded-2xl overflow-hidden border border-white/10">
                <iframe title="map" src={MAP(e.venue.address || `${e.city}, ${e.state}`)} className="w-full h-72 grayscale invert-[.9]" loading="lazy" />
              </div>
            </Reveal>
          )}

          {e.sponsors?.length > 0 && <Reveal><Sponsors items={e.sponsors} /></Reveal>}

          {e.related_events?.length > 0 && (
            <Reveal>
              <SectionTitle kicker="Keep the night going" title="Related events" />
              <div className="grid sm:grid-cols-2 gap-4 mt-6">
                {e.related_events.map((r) => (
                  <Link key={r.id} to={`/events/${r.slug}`} className="flex gap-3 bg-[#111] border border-white/10 rounded-xl p-3 card-hover">
                    <img src={fileUrl(r.profile_image || r.event_image)} alt="" className="w-20 h-20 rounded-lg object-cover" />
                    <div><p className="font-semibold">{r.title}</p><p className="text-sm text-zinc-400">{fmtDate(r.event_date)} · {r.city}</p></div>
                  </Link>
                ))}
              </div>
            </Reveal>
          )}
        </div>

        {/* Ticket sidebar */}
        <aside data-testid="event-ticket-panel">
          <div className="lg:sticky lg:top-20 space-y-6">
          <div className="bg-gradient-to-b from-[#161616] to-[#0d0d0d] border border-white/10 rounded-3xl p-7 glow-ring">
            {e.ticket_link && (<>
              <p className="text-zinc-400 text-sm">Ticket price</p>
              <p className="font-display font-black text-4xl mb-2 gradient-text">{isFree ? "Free" : `$${e.ticket_price}`}</p>
            </>)}
            {e.rsvp_enabled && <p className="text-sm text-zinc-400 mb-5 flex items-center gap-1.5" data-testid="rsvp-count"><Users size={14} /> {e.rsvp_count || 0} going</p>}
            {e.ticket_link && (
              <a href={e.ticket_link} target="_blank" rel="noreferrer" className="block text-center bg-rose-500 hover:bg-rose-600 font-bold py-4 rounded-xl transition-colors" data-testid="event-buy-btn">
                <Ticket className="inline mr-2" size={18} />{isFree ? "Get free ticket" : "Buy Tickets"}
              </a>
            )}
            {e.rsvp_enabled && <div className="mt-3"><RsvpDialog event={e} onDone={load} label="RSVP" /></div>}
            <button onClick={share} className="w-full mt-3 border border-white/20 hover:bg-white/5 font-medium py-3 rounded-xl flex items-center justify-center gap-2" data-testid="event-share-btn"><Share2 size={16} /> Share</button>
            <div className="mt-6 pt-5 border-t border-white/10 text-sm text-zinc-400 space-y-2">
              <p>Promoter: <span className="text-white">{e.promoter_name}</span></p>
              {e.promoter_contact && (() => {
                const c = String(e.promoter_contact).trim();
                const isEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(c);
                const digits = c.replace(/[^+\d]/g, "");
                const isPhone = !isEmail && digits.length >= 7;
                const href = isEmail ? `mailto:${c}` : isPhone ? `tel:${digits}` : null;
                return (
                  <p>Contact: {href ? (
                    <a href={href} className="text-rose-400 hover:text-rose-300 hover:underline" data-testid="event-promoter-contact-link">{c}</a>
                  ) : <span className="text-white">{c}</span>}</p>
                );
              })()}
            </div>
          </div>
          <WeatherPanel city={e.city} state={e.state} date={e.event_date} />
          </div>
        </aside>
      </div>

      {/* Mobile sticky buy bar */}
      <div className="lg:hidden fixed bottom-0 inset-x-0 z-40 glass sticky-bar px-5 py-3 flex items-center justify-between gap-3" data-testid="mobile-buy-bar">
        <div>{e.ticket_link ? <><p className="text-xs text-zinc-400">Ticket</p><p className="font-display font-black text-xl">{isFree ? "Free" : `$${e.ticket_price}`}</p></> : <p className="text-xs text-zinc-400">{e.title}</p>}</div>
        {e.rsvp_enabled ? <RsvpDialog event={e} onDone={load} label="RSVP" className="flex-1 max-w-[60%] text-center bg-rose-500 font-bold py-3 rounded-xl" />
          : e.ticket_link ? <a href={e.ticket_link} target="_blank" rel="noreferrer" className="flex-1 max-w-[60%] text-center bg-rose-500 font-bold py-3 rounded-xl" data-testid="mobile-buy-btn"><Ticket className="inline mr-2" size={16} />Tickets</a>
          : null}
      </div>
    </div>
  );
}
